// Some MIME types reported by the browser are not on the allowed list in the media platform.
// This map remaps them to their accepted equivalents.
const MIME_TYPE_OVERRIDES: Record<string, string> = {
  'image/x-pentax-pef': 'image/x-raw-pentax',
  'image/x-panasonic-rw2': 'image/x-raw-panasonic',
  'image/x-minolta-mrw': 'image/x-raw-minolta',
};

export const resolveUploadMimeType = (file: File): string => MIME_TYPE_OVERRIDES[file.type] ?? file.type;

export const enum SupportedFileType {
  Image = 'image',
  Gif = 'gif',
  Video = 'video',
}

// all details can be found here https://support.wix.com/en/article/wix-media-supported-media-file-types-and-file-sizes
export const MAX_SIZE: Record<SupportedFileType, number> = {
  [SupportedFileType.Image]: 25 * 1024 * 1024, // 25MB
  [SupportedFileType.Gif]: 15 * 1024 * 1024, // 15MB
  [SupportedFileType.Video]: 4000 * 1024 * 1024, // 4GB
};
export const LIMIT_ATTACH_IMAGES = 7;
export const LEARN_MORE_ARTICLE_URL =
  'https://support.wix.com/article/wix-media-supported-media-file-types-and-file-sizes';

// https://support.wix.com/en/article/wix-media-supported-media-file-types-and-file-sizes?tabs=Videos
export const SUPPORTED_VIDEO_FILES = [
  'AVI',
  'MPEG',
  'MPG',
  'MPE',
  'MP4',
  'MKV',
  'WebM',
  'MOV',
  'OGV',
  'VOB',
  'M4V',
  '3GP',
  '3GPP',
  'DivX',
  'XVID',
  'MXF',
  'WMV',
  'M1V',
  'FLV',
  'M2TS',
];

// https://support.wix.com/en/article/wix-media-supported-media-file-types-and-file-sizes?tabs=Images
export const SUPPORTED_IMAGE_FILES = [
  'JPG',
  'PNG',
  'GIF',
  'JPEG',
  'JPE',
  'JFIF',
  'BMP',
  'HEIC',
  'HEIF',
  'TIFF',
  'TIF',
  'WEBP',
  // JPEG 2000 formats
  'jp2',
  'jpg2',
  'j2k',
  'jpf',
  'jpm',
  'j2c',
  'jpc',
  'jpx',
  // RAW formats
  'arw',
  'srw',
  'nef',
  'cr2',
  'cr3',
  'crw',
  'rwl',
  'rw2',
  'raw',
  'raf',
  'pef',
  'orf',
  'mrw',
  'dng',
  'sr2',
  'srf',
  'kdc',
  'k25',
  'dcr',
  'x3f',
  'erf',
  '3fr',
];

const supportedExtensions = new Set(
  [...SUPPORTED_VIDEO_FILES, ...SUPPORTED_IMAGE_FILES].map((e) => e.toLowerCase()),
);

export const isSupportedAttachmentFile = (file: File): boolean => {
  const dotIndex = file.name.lastIndexOf('.');
  if (dotIndex === -1) {
    return false;
  }
  return supportedExtensions.has(file.name.slice(dotIndex + 1).toLowerCase());
};

const videoExtensions = new Set(SUPPORTED_VIDEO_FILES.map((e) => e.toLowerCase()));

// Browsers don't always report file.type for videos (e.g. some .mp4 come back as ''), which would
// leave the probe unable to pick the video branch. Fall back to the file extension in that case.
export const isVideoFile = (file: File): boolean => {
  const dotIndex = file.name.lastIndexOf('.');
  if (dotIndex === -1) {
    return false;
  }
  return videoExtensions.has(file.name.slice(dotIndex + 1).toLowerCase());
};
